import React, { useState, useEffect, useRef, useMemo } from "react";
import supabase from "../supabaseClient";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import Protegido from "../components/Protegido";
import { useNavigationState } from "../context/NavigationContext";
import useLimites from "../hooks/useLimites";
import EditarPaqueteModal from "../components/EditarPaqueteModal";
import { fechaHoy } from "../utils/internacional";

const money = (n) => `$${Number(n || 0).toLocaleString("es-CO")}`;

export default function Inventario() {
  const { saveModuleState, getModuleState } = useNavigationState();
  const estadoGuardado = useRef(getModuleState("/inventario")).current;

  const [productos, setProductos] = useState([]);
  const [form, setForm] = useState(estadoGuardado?.form || {
    nombre: "", descripcion: "", precio: "", stock: "", categoria: "",
    tipo: "articulo", costo: "",
    valor_adquisicion: "", fecha_adquisicion: "", registrar_gasto_compra: false
  });
  const [buscar, setBuscar] = useState(estadoGuardado?.buscar || "");
  const [categoriaFiltro, setCategoriaFiltro] = useState(estadoGuardado?.categoriaFiltro || "");
  const [editandoId, setEditandoId] = useState(estadoGuardado?.editandoId || null);
  const [mostrarFormulario, setMostrarFormulario] = useState(estadoGuardado?.mostrarFormulario || false);
  const [loading, setLoading] = useState(true);
  const { puedeCrearProducto, mensajeBloqueo } = useLimites();

  // ── Paquetes de eventos ──
  const [tabActivo, setTabActivo] = useState(estadoGuardado?.tabActivo || "todos");
  const [paquetes, setPaquetes] = useState([]);
  const [loadingPaquetes, setLoadingPaquetes] = useState(false);
  const [modalPaquete, setModalPaquete] = useState(false);
  const [paqueteEditar, setPaqueteEditar] = useState(null);

  // Guardar estado
  useEffect(() => {
    saveModuleState("/inventario", { form, buscar, categoriaFiltro, editandoId, mostrarFormulario, tabActivo });
  }, [JSON.stringify(form), buscar, categoriaFiltro, editandoId, mostrarFormulario, tabActivo, saveModuleState]);

  useEffect(() => { obtenerProductos(); obtenerPaquetes(); }, []);

  const obtenerProductos = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("productos")
      .select("id, nombre, descripcion, precio, stock, categoria, tipo, costo, valor_adquisicion, fecha_adquisicion")
      .order("nombre")
      .limit(1500);
    if (error) console.error("Error al obtener productos:", error);
    else setProductos(data || []);
    setLoading(false);
  };

  // ── Paquetes CRUD ──
  const obtenerPaquetes = async () => {
    setLoadingPaquetes(true);
    const { data, error } = await supabase.from("paquetes_eventos").select("*").order("nombre");
    if (error) console.error("Error al obtener paquetes:", error);
    else setPaquetes(data || []);
    setLoadingPaquetes(false);
  };

  const eliminarPaquete = async (id, nombre) => {
    const { isConfirmed } = await Swal.fire({
      title: `¿Eliminar "${nombre}"?`,
      text: "El paquete se eliminará permanentemente. Los pedidos que ya usaron su contenido NO se afectan.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      confirmButtonColor: "#ef4444",
      cancelButtonText: "Cancelar",
    });
    if (!isConfirmed) return;
    const { error } = await supabase.from("paquetes_eventos").delete().eq("id", id);
    if (!error) {
      Swal.fire({ icon: "success", title: "Paquete eliminado", timer: 1500, showConfirmButton: false });
      obtenerPaquetes();
    } else {
      Swal.fire("Error", "No se pudo eliminar el paquete.", "error");
    }
  };

  const abrirEditarPaquete = (paquete) => {
    setPaqueteEditar(paquete);
    setModalPaquete(true);
  };

  const abrirNuevoPaquete = () => {
    setPaqueteEditar(null);
    setModalPaquete(true);
  };

  /* ─── Guardar producto (con campos completos + contabilidad) ─── */
  const guardarProducto = async () => {
    if (!editandoId && !puedeCrearProducto()) {
      const msg = mensajeBloqueo("producto");
      return Swal.fire(msg.titulo, msg.mensaje, msg.icono);
    }
    const { nombre, descripcion, precio, stock, categoria, tipo, costo } = form;
    const esServicio = tipo === "servicio";
    const stockFinal = esServicio ? 999 : Number(stock);
    if (!nombre || !precio || (!esServicio && !stock)) {
      return Swal.fire("Campos requeridos", esServicio ? "Nombre y precio son obligatorios." : "Nombre, precio y stock son obligatorios.", "warning");
    }
    if (precio < 0 || stockFinal < 0) {
      return Swal.fire("Valores inválidos", "Precio y stock deben ser positivos.", "error");
    }
    const valorAdq = Number(form.valor_adquisicion || 0);
    const fechaAdq = form.fecha_adquisicion || null;
    const registrarGasto = !!form.registrar_gasto_compra;
    const datos = {
      nombre, descripcion, precio: Number(precio), stock: stockFinal, categoria, tipo,
      costo: Number(costo || 0),
      valor_adquisicion: valorAdq, fecha_adquisicion: fechaAdq,
      registrar_gasto_compra: registrarGasto,
    };
    const operacion = editandoId
      ? supabase.from("productos").update(datos).eq("id", editandoId)
      : supabase.from("productos").insert([datos]);
    const { error } = await operacion;
    if (!error) {
      // 📊 Registrar gasto en contabilidad si el checkbox está activo y es creación nueva
      if (!editandoId && registrarGasto && valorAdq > 0) {
        const totalGasto = valorAdq * stockFinal;
        try {
          await supabase.from("movimientos_contables").insert([{
            fecha: fechaAdq || fechaHoy(),
            tipo: "gasto",
            monto: totalGasto,
            descripcion: `Compra de inventario: ${nombre} (${stockFinal} und × ${money(valorAdq)})`,
            categoria: "Compra de inventario",
            estado: "activo",
          }]);
        } catch (err) {
          console.error("Error registrando gasto de compra:", err);
        }
      }
      Swal.fire({ icon: "success", title: editandoId ? "Actualizado" : "Guardado", timer: 1500, showConfirmButton: false });
      limpiarFormulario();
      obtenerProductos();
    } else Swal.fire("Error", "No se pudo guardar el producto.", "error");
  };

  // ➕ Registrar una compra de más unidades de un artículo que ya existe.
  // Va aparte de "editar" a propósito: editar corrige datos y NUNCA toca
  // contabilidad; comprar suma stock y sí genera el gasto correspondiente.
  const registrarCompra = async (producto) => {
    const h = new Date();
    const fechaHoy = `${h.getFullYear()}-${String(h.getMonth() + 1).padStart(2, "0")}-${String(h.getDate()).padStart(2, "0")}`;

    const { value: datos } = await Swal.fire({
      title: "➕ Registrar compra",
      html: `
        <div style="text-align:left;font-size:14px">
          <p style="margin:0 0 14px;color:#6b7280">
            <strong>${producto.nombre}</strong><br>Stock actual: <strong>${Number(producto.stock || 0)}</strong>
          </p>
          <label style="font-weight:600">¿Cuántas unidades compró?</label>
          <input id="sw-cantidad" type="number" min="1"
                 style="width:100%;padding:8px;margin:4px 0 14px;border:1px solid #d1d5db;border-radius:8px;box-sizing:border-box"
                 placeholder="Ej: 50">
          <label style="font-weight:600">Valor de cada unidad</label>
          <input id="sw-valor" type="number" min="0"
                 style="width:100%;padding:8px;margin:4px 0 14px;border:1px solid #d1d5db;border-radius:8px;box-sizing:border-box"
                 value="${Number(producto.valor_adquisicion || 0) || ""}" placeholder="Ej: 20000">
          <label style="font-weight:600">Fecha de la compra</label>
          <input id="sw-fecha" type="date"
                 style="width:100%;padding:8px;margin:4px 0;border:1px solid #d1d5db;border-radius:8px;box-sizing:border-box"
                 value="${fechaHoy}">
        </div>`,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: "Registrar compra",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#16a34a",
      preConfirm: () => {
        const cantidad = Number(document.getElementById("sw-cantidad").value || 0);
        const valor = Number(document.getElementById("sw-valor").value || 0);
        const fecha = document.getElementById("sw-fecha").value;
        if (cantidad <= 0) return Swal.showValidationMessage("La cantidad debe ser mayor a 0");
        if (valor <= 0) return Swal.showValidationMessage("El valor unitario debe ser mayor a 0");
        if (!fecha) return Swal.showValidationMessage("Falta la fecha de la compra");
        return { cantidad, valor, fecha };
      },
    });

    if (!datos) return;

    const { cantidad, valor, fecha } = datos;
    const stockAnterior = Number(producto.stock || 0);
    const stockNuevo = stockAnterior + cantidad;
    const totalGasto = cantidad * valor;

    // 1️⃣ Primero el inventario. Si esto falla, no se registra ningún gasto.
    const { error: errorProducto } = await supabase
      .from("productos")
      .update({ stock: stockNuevo, valor_adquisicion: valor, fecha_adquisicion: fecha })
      .eq("id", producto.id);

    if (errorProducto) {
      return Swal.fire("Error", "No se pudo actualizar el inventario. No se registró ningún gasto.", "error");
    }

    // 2️⃣ Después el gasto en contabilidad
    const { error: errorGasto } = await supabase.from("movimientos_contables").insert([{
      fecha,
      tipo: "gasto",
      monto: totalGasto,
      descripcion: `Compra de inventario: ${producto.nombre} (${cantidad} und × ${money(valor)})`,
      categoria: "Compra de inventario",
      estado: "activo",
    }]);

    if (errorGasto) {
      console.error("Error registrando gasto de compra:", errorGasto);
      Swal.fire(
        "Stock actualizado",
        `El stock quedó en ${stockNuevo}, pero el gasto de ${money(totalGasto)} no se pudo registrar. Agrégalo manualmente en Contabilidad.`,
        "warning"
      );
    } else {
      Swal.fire({
        icon: "success",
        title: "Compra registrada",
        html: `Stock: ${stockAnterior} → <strong>${stockNuevo}</strong><br>Gasto: <strong>${money(totalGasto)}</strong>`,
        timer: 2600,
        showConfirmButton: false,
      });
    }

    obtenerProductos();
  };

  const eliminarProducto = async (id) => {
    const { isConfirmed } = await Swal.fire({
      title: "¿Eliminar este producto?",
      text: "Esta acción no se puede deshacer",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      confirmButtonColor: "#ef4444",
      cancelButtonText: "Cancelar",
    });
    if (!isConfirmed) return;
    const { error } = await supabase.from("productos").delete().eq("id", id);
    if (!error) {
      Swal.fire({ icon: "success", title: "Eliminado", timer: 1500, showConfirmButton: false });
      obtenerProductos();
    }
  };

  const editarProducto = (producto) => {
    setEditandoId(producto.id);
    setForm({
      nombre: producto.nombre || "",
      descripcion: producto.descripcion || "",
      precio: producto.precio,
      stock: producto.tipo === "servicio" ? 999 : producto.stock,
      categoria: producto.categoria || "",
      tipo: producto.tipo || "articulo",
      costo: producto.costo || "",
      valor_adquisicion: producto.valor_adquisicion || "",
      fecha_adquisicion: producto.fecha_adquisicion || "",
      registrar_gasto_compra: false, // Al editar nunca re-registrar el gasto
    });
    setMostrarFormulario(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const limpiarFormulario = () => {
    setForm({
      nombre: "", descripcion: "", precio: "", stock: "", categoria: "",
      tipo: "articulo", costo: "",
      valor_adquisicion: "", fecha_adquisicion: "", registrar_gasto_compra: false
    });
    setEditandoId(null);
    setMostrarFormulario(false);
  };

  const borrarTodo = async () => {
    if (productos.length === 0) return Swal.fire("Sin datos", "No hay productos para eliminar.", "info");
    const { isConfirmed } = await Swal.fire({
      title: "⚠️ ¿Eliminar TODO el inventario?",
      html: `<p>Esta acción borrará <strong>${productos.length} producto(s)</strong> y no se puede deshacer.</p>
             <p style="color:#ef4444;font-weight:600;margin-top:8px;">Los documentos que ya usen estos productos NO se afectan.</p>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar todo",
      confirmButtonColor: "#ef4444",
      cancelButtonText: "Cancelar",
    });
    if (!isConfirmed) return;
    const { error } = await supabase.from("productos").delete().neq("id", "00000000-0000-0000-0000-000000000000");
    if (!error) {
      Swal.fire({ icon: "success", title: "Inventario eliminado", timer: 2000, showConfirmButton: false });
      obtenerProductos();
    } else Swal.fire("Error", "No se pudo eliminar el inventario.", "error");
  };

  /* ─── Exportar Excel (.xlsx) — con todos los campos ─── */
  const exportarExcel = () => {
    if (productos.length === 0) return Swal.fire("Sin datos", "No hay productos para exportar.", "info");

    const datosLimpios = productos.map((p, i) => ({
      "#": i + 1,
      "Tipo": (p.tipo || "articulo") === "servicio" ? "Servicio" : "Artículo",
      "Nombre": p.nombre || "",
      "Descripción": p.descripcion || "",
      "Precio": Number(p.precio || 0),
      "Costo": Number(p.costo || 0),
      "V. Adquisición": Number(p.valor_adquisicion || 0),
      "Fecha Adq.": p.fecha_adquisicion || "",
      "Stock": (p.tipo || "articulo") === "servicio" ? "∞" : Number(p.stock || 0),
      "Categoría": p.categoria || ""
    }));

    const ws = XLSX.utils.json_to_sheet(datosLimpios);
    ws["!cols"] = [
      { wch: 5 }, { wch: 10 }, { wch: 30 }, { wch: 30 }, { wch: 14 },
      { wch: 14 }, { wch: 14 }, { wch: 12 }, { wch: 8 }, { wch: 20 }
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Inventario");
    const fecha = new Date().toLocaleDateString("es-CO").replaceAll("/", "-");
    XLSX.writeFile(wb, `inventario_${fecha}.xlsx`);
    Swal.fire({ icon: "success", title: "Excel descargado", timer: 1500, showConfirmButton: false });
  };

  /* ─── Importar desde Excel — reconoce campos extendidos ─── */
  const importarDesdeExcel = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    event.target.value = "";

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: "array" });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

      const mapCol = (row, opciones) => {
        for (const key of opciones) {
          const val = row[key] || row[key.toLowerCase()] || row[key.toUpperCase()];
          if (val !== undefined && val !== "") return String(val).trim();
        }
        return "";
      };

      const productosValidos = json
        .map((row) => {
          const tipoRaw = mapCol(row, ["tipo", "Tipo", "TIPO"]).toLowerCase();
          const esServicio = tipoRaw === "servicio" || tipoRaw === "service";
          return {
            nombre: mapCol(row, ["nombre", "Nombre", "NOMBRE", "producto", "Producto", "PRODUCTO"]),
            descripcion: mapCol(row, ["descripcion", "Descripción", "Descripcion", "DESCRIPCION"]),
            precio: parseFloat(mapCol(row, ["precio", "Precio", "PRECIO", "price", "valor", "Valor"]) || "0"),
            stock: esServicio ? 999 : parseInt(mapCol(row, ["stock", "Stock", "STOCK", "cantidad", "Cantidad", "CANTIDAD", "unidades"]) || "0", 10),
            categoria: mapCol(row, ["categoria", "Categoría", "Categoria", "CATEGORIA"]),
            tipo: esServicio ? "servicio" : "articulo",
            costo: parseFloat(mapCol(row, ["costo", "Costo", "COSTO", "costo_interno", "Costo interno"]) || "0"),
            valor_adquisicion: parseFloat(mapCol(row, ["valor_adquisicion", "Valor adquisicion", "Valor Adquisición", "V. Adquisición", "valor_compra", "Valor compra", "costo_compra", "Costo compra"]) || "0"),
            fecha_adquisicion: mapCol(row, ["fecha_adquisicion", "Fecha adquisicion", "Fecha Adquisición", "Fecha compra", "fecha_compra", "Fecha Adq."]) || null,
          };
        })
        .filter((p) => p.nombre && !isNaN(p.precio) && p.precio >= 0 && !isNaN(p.stock) && p.stock >= 0);

      if (productosValidos.length === 0) {
        return Swal.fire("Sin datos válidos", "No se encontraron productos con nombre, precio y stock válidos.", "error");
      }

      const { error } = await supabase.from("productos").insert(productosValidos);
      if (error) {
        console.error(error);
        return Swal.fire("Error", "No se pudieron importar los productos.", "error");
      }
      Swal.fire("Importación exitosa", `${productosValidos.length} producto(s) importados correctamente.`, "success");
      obtenerProductos();
    } catch (err) {
      console.error(err);
      Swal.fire("Error al leer archivo", "Verifica que sea un archivo Excel (.xlsx) válido.", "error");
    }
  };

  /* ─── Guía de importación (con campos extendidos) ─── */
  const mostrarGuia = () => {
    Swal.fire({
      title: "📥 Guía de importación desde Excel",
      html: `
        <div style="text-align:left;font-size:13px;line-height:1.7;">
          <p>Tu archivo Excel debe tener estas columnas en la <strong>primera fila</strong>:</p>
          <table style="width:100%;border-collapse:collapse;margin:10px 0;font-size:12px;">
            <tr style="background:#f0f9ff;">
              <th style="border:1px solid #e5e7eb;padding:6px;text-align:left;">Columna</th>
              <th style="border:1px solid #e5e7eb;padding:6px;text-align:left;">¿Obligatoria?</th>
              <th style="border:1px solid #e5e7eb;padding:6px;text-align:left;">Ejemplo</th>
            </tr>
            <tr>
              <td style="border:1px solid #e5e7eb;padding:6px;font-weight:600;">nombre</td>
              <td style="border:1px solid #e5e7eb;padding:6px;color:#ef4444;">✅ Sí</td>
              <td style="border:1px solid #e5e7eb;padding:6px;">Silla Tiffany</td>
            </tr>
            <tr>
              <td style="border:1px solid #e5e7eb;padding:6px;font-weight:600;">precio</td>
              <td style="border:1px solid #e5e7eb;padding:6px;color:#ef4444;">✅ Sí</td>
              <td style="border:1px solid #e5e7eb;padding:6px;">15000</td>
            </tr>
            <tr>
              <td style="border:1px solid #e5e7eb;padding:6px;font-weight:600;">stock</td>
              <td style="border:1px solid #e5e7eb;padding:6px;color:#ef4444;">✅ Sí (artículos)</td>
              <td style="border:1px solid #e5e7eb;padding:6px;">50</td>
            </tr>
            <tr>
              <td style="border:1px solid #e5e7eb;padding:6px;">tipo</td>
              <td style="border:1px solid #e5e7eb;padding:6px;color:#9ca3af;">Opcional</td>
              <td style="border:1px solid #e5e7eb;padding:6px;">articulo / servicio</td>
            </tr>
            <tr>
              <td style="border:1px solid #e5e7eb;padding:6px;">costo</td>
              <td style="border:1px solid #e5e7eb;padding:6px;color:#9ca3af;">Opcional</td>
              <td style="border:1px solid #e5e7eb;padding:6px;">80000</td>
            </tr>
            <tr>
              <td style="border:1px solid #e5e7eb;padding:6px;">descripcion</td>
              <td style="border:1px solid #e5e7eb;padding:6px;color:#9ca3af;">Opcional</td>
              <td style="border:1px solid #e5e7eb;padding:6px;">Silla dorada elegante</td>
            </tr>
            <tr>
              <td style="border:1px solid #e5e7eb;padding:6px;">categoria</td>
              <td style="border:1px solid #e5e7eb;padding:6px;color:#9ca3af;">Opcional</td>
              <td style="border:1px solid #e5e7eb;padding:6px;">Mobiliario</td>
            </tr>
            <tr style="background:#fefce8;">
              <td style="border:1px solid #e5e7eb;padding:6px;">valor_adquisicion</td>
              <td style="border:1px solid #e5e7eb;padding:6px;color:#9ca3af;">Opcional</td>
              <td style="border:1px solid #e5e7eb;padding:6px;">85000</td>
            </tr>
            <tr style="background:#fefce8;">
              <td style="border:1px solid #e5e7eb;padding:6px;">fecha_adquisicion</td>
              <td style="border:1px solid #e5e7eb;padding:6px;color:#9ca3af;">Opcional</td>
              <td style="border:1px solid #e5e7eb;padding:6px;">2026-01-15</td>
            </tr>
          </table>
          <p style="margin-top:10px;">💡 <strong>Tips:</strong></p>
          <ul style="margin:4px 0 0 16px;padding:0;">
            <li>El sistema acepta variantes como "Nombre", "NOMBRE", "Producto", "cantidad", "Valor", etc.</li>
            <li>Si la columna <strong>tipo</strong> dice "servicio", el stock se ignora (se pone ilimitado).</li>
            <li>La columna <strong>costo</strong> registra el costo interno de producción/prestación.</li>
            <li><strong>valor_adquisicion</strong> = lo que pagaste por comprar el artículo (valor real del inventario).</li>
            <li><strong>fecha_adquisicion</strong> = fecha de compra en formato AAAA-MM-DD (ej: 2026-01-15).</li>
            <li>⚠️ La importación desde Excel <strong>NO registra gastos</strong> en contabilidad. Si necesitas registrar el gasto, edita cada artículo y activa el checkbox.</li>
            <li>Precio y stock deben ser números positivos.</li>
            <li>Las filas sin nombre o precio serán ignoradas.</li>
            <li>Formatos aceptados: <strong>.xlsx</strong> y <strong>.xls</strong></li>
          </ul>
        </div>
      `,
      width: 560,
      confirmButtonText: "Entendido",
      confirmButtonColor: "#0077B6"
    });
  };

  /* ─── Filtrar ─── */
  const categoriasUnicas = useMemo(() =>
    [...new Set(productos.map((p) => p.categoria).filter(Boolean))].sort(),
    [productos]
  );

  const productosFiltrados = useMemo(() => {
    let lista = productos;
    // Filtrar por tipo según tab activo
    if (tabActivo === "articulo") {
      lista = lista.filter((p) => (p.tipo || "articulo") === "articulo");
    } else if (tabActivo === "servicio") {
      lista = lista.filter((p) => p.tipo === "servicio");
    }
    if (buscar.trim()) {
      const q = buscar.toLowerCase();
      lista = lista.filter((p) =>
        (p.nombre || "").toLowerCase().includes(q) ||
        (p.descripcion || "").toLowerCase().includes(q) ||
        (p.categoria || "").toLowerCase().includes(q)
      );
    }
    if (categoriaFiltro) {
      lista = lista.filter((p) => (p.categoria || "").toLowerCase() === categoriaFiltro.toLowerCase());
    }
    return lista;
  }, [productos, buscar, categoriaFiltro, tabActivo]);

  /* ─── KPIs ─── */
  const articulos = productos.filter((p) => (p.tipo || "articulo") === "articulo");
  const servicios = productos.filter((p) => p.tipo === "servicio");
  const stockTotal = articulos.reduce((s, p) => s + Number(p.stock || 0), 0);
  const valorReal = articulos.reduce((s, p) => s + (Number(p.valor_adquisicion || 0) * Number(p.stock || 0)), 0);
  const valorAlquiler = articulos.reduce((s, p) => s + (Number(p.precio || 0) * Number(p.stock || 0)), 0);

  /* ═══ RENDER ═══ */
  return (
    <Protegido>
      <div className="sw-pagina">
        <div className="sw-pagina-contenido" style={{ maxWidth: 950 }}>

          {/* ═══ HEADER ═══ */}
          <div className="sw-header">
            <h1 className="sw-header-titulo">📦 Gestión de Inventario</h1>
          </div>

          {/* ═══ TABS ═══ */}
          <div style={{
            display: "flex", gap: 0, marginBottom: 16, borderRadius: 10,
            border: "1px solid var(--sw-borde)", overflow: "hidden", background: "white",
          }}>
            {[
              { id: "todos", label: "📋 Todos", count: productos.length },
              { id: "articulo", label: "📦 Artículos", count: articulos.length },
              { id: "servicio", label: "🔧 Servicios", count: servicios.length },
              { id: "paquete", label: "🎁 Paquetes", count: paquetes.length },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setTabActivo(tab.id)}
                style={{
                  flex: 1, padding: "10px 8px", border: "none", cursor: "pointer",
                  fontSize: 12, fontWeight: 600, transition: "all 0.15s",
                  background: tabActivo === tab.id
                    ? (tab.id === "paquete" ? "linear-gradient(135deg, #0891b2, #0e7490)" : "var(--sw-gradiente-primario)")
                    : "transparent",
                  color: tabActivo === tab.id ? "white" : "var(--sw-texto-secundario)",
                  borderRight: "1px solid var(--sw-borde)",
                }}
              >
                {tab.label} <span style={{ opacity: 0.8 }}>({tab.count})</span>
              </button>
            ))}
          </div>

          {/* ═══ VISTA PAQUETES ═══ */}
          {tabActivo === "paquete" ? (
            <>
              {/* KPIs Paquetes */}
              <div style={{
                display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                gap: 12, marginBottom: 20,
              }}>
                <div className="sw-card" style={{ padding: "16px 14px", textAlign: "center" }}>
                  <div style={{ fontSize: 12, color: "var(--sw-texto-terciario)", fontWeight: 500 }}>Paquetes</div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: "#0891b2", marginTop: 4 }}>{paquetes.length}</div>
                </div>
                <div className="sw-card" style={{ padding: "16px 14px", textAlign: "center" }}>
                  <div style={{ fontSize: 12, color: "var(--sw-texto-terciario)", fontWeight: 500 }}>Precio promedio</div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: "#0e7490", marginTop: 4 }}>
                    {money(paquetes.length > 0 ? paquetes.reduce((s, p) => s + Number(p.precio_total || 0), 0) / paquetes.length : 0)}
                  </div>
                </div>
              </div>

              {/* Acciones Paquetes */}
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
                <button
                  onClick={abrirNuevoPaquete}
                  style={{
                    padding: "10px 18px", borderRadius: 8, border: "none", cursor: "pointer",
                    fontWeight: 600, fontSize: 13, color: "white", display: "inline-flex",
                    alignItems: "center", gap: 6,
                    background: "linear-gradient(135deg, #0891b2, #0e7490)",
                    boxShadow: "0 2px 6px rgba(8,145,178,0.25)",
                    transition: "all 0.2s",
                  }}
                >
                  ＋ Nuevo paquete
                </button>
              </div>

              {/* Lista Paquetes */}
              <div className="sw-card">
                <div className="sw-card-header">
                  <h3 className="sw-card-titulo" style={{ color: "#0e7490" }}>
                    🎁 Paquetes de Eventos ({paquetes.length})
                  </h3>
                </div>
                <div className="sw-card-body" style={{ padding: 0 }}>
                  {loadingPaquetes ? (
                    <div style={{ padding: 40, textAlign: "center", color: "var(--sw-texto-terciario)" }}>
                      Cargando paquetes...
                    </div>
                  ) : paquetes.length === 0 ? (
                    <div style={{ padding: 40, textAlign: "center" }}>
                      <div style={{ fontSize: 40, marginBottom: 8 }}>🎁</div>
                      <div style={{ fontSize: 14, color: "var(--sw-texto-terciario)" }}>
                        No hay paquetes creados
                      </div>
                      <p style={{ fontSize: 12, color: "var(--sw-texto-terciario)", marginTop: 6 }}>
                        Crea tu primer paquete con los productos que más uses juntos
                      </p>
                    </div>
                  ) : (
                    paquetes.map((paq) => {
                      const totalItems = (paq.productos || []).reduce((acc, item) => {
                        if (item.es_grupo && Array.isArray(item.productos)) return acc + item.productos.length;
                        return acc + 1;
                      }, 0);
                      return (
                        <div
                          key={paq.id}
                          style={{
                            padding: "14px 16px", borderBottom: "1px solid #f3f4f6",
                            display: "flex", justifyContent: "space-between", alignItems: "center",
                            gap: 10, transition: "background 0.15s",
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = "#f0fdfa")}
                          onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                        >
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                              <span style={{ fontWeight: 600, fontSize: 14, color: "var(--sw-texto)" }}>
                                {paq.nombre}
                              </span>
                              <span style={{
                                fontSize: 10, padding: "2px 8px", borderRadius: 20,
                                background: "#f0fdfa", color: "#0891b2", fontWeight: 600,
                              }}>
                                {totalItems} items
                              </span>
                            </div>
                            <div style={{
                              fontSize: 12, color: "var(--sw-texto-terciario)", marginTop: 3,
                              display: "flex", flexWrap: "wrap", gap: "4px 14px",
                            }}>
                              <span style={{ fontWeight: 600, color: "#059669" }}>{money(paq.precio_total)}</span>
                              {paq.descripcion && <span style={{ color: "#9ca3af" }}>{paq.descripcion}</span>}
                              <span style={{ color: "#d1d5db" }}>
                                {new Date(paq.created_at).toLocaleDateString("es-CO")}
                              </span>
                            </div>
                          </div>

                          <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                            <button className="sw-btn-icono" onClick={() => abrirEditarPaquete(paq)} title="Editar paquete">
                              ✏️
                            </button>
                            <button
                              className="sw-btn-icono"
                              onClick={() => eliminarPaquete(paq.id, paq.nombre)}
                              title="Eliminar paquete"
                              style={{ color: "#ef4444" }}
                            >
                              🗑️
                            </button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </>
          ) : (
          <>
          {/* ═══ KPI CARDS (productos) — Artículos, Servicios, Stock, Valor real, Paquetes ═══ */}
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
            gap: 12,
            marginBottom: 20
          }}>
            <div className="sw-card" style={{ padding: "16px 14px", textAlign: "center" }}>
              <div style={{ fontSize: 12, color: "var(--sw-texto-terciario)", fontWeight: 500 }}>Artículos</div>
              <div style={{ fontSize: 24, fontWeight: 700, color: "var(--sw-azul)", marginTop: 4 }}>{articulos.length}</div>
            </div>
            <div className="sw-card" style={{ padding: "16px 14px", textAlign: "center" }}>
              <div style={{ fontSize: 12, color: "var(--sw-texto-terciario)", fontWeight: 500 }}>Servicios</div>
              <div style={{ fontSize: 24, fontWeight: 700, color: "#7c3aed", marginTop: 4 }}>{servicios.length}</div>
            </div>
            <div className="sw-card" style={{ padding: "16px 14px", textAlign: "center" }}>
              <div style={{ fontSize: 12, color: "var(--sw-texto-terciario)", fontWeight: 500 }}>Stock total</div>
              <div style={{ fontSize: 24, fontWeight: 700, color: "var(--sw-verde)", marginTop: 4 }}>{stockTotal.toLocaleString("es-CO")}</div>
            </div>
            <div className="sw-card" style={{ padding: "16px 14px", textAlign: "center" }}>
              <div style={{ fontSize: 12, color: "var(--sw-texto-terciario)", fontWeight: 500 }}>Valor real inventario</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: "var(--sw-morado)", marginTop: 4 }}>{money(valorReal)}</div>
              {valorAlquiler > 0 && (
                <div style={{ fontSize: 10, color: "#9ca3af", marginTop: 2 }}>Alquiler: {money(valorAlquiler)}</div>
              )}
            </div>
            <div className="sw-card" style={{ padding: "16px 14px", textAlign: "center" }}>
              <div style={{ fontSize: 12, color: "var(--sw-texto-terciario)", fontWeight: 500 }}>Paquetes</div>
              <div style={{ fontSize: 24, fontWeight: 700, color: "#0891b2", marginTop: 4 }}>{paquetes.length}</div>
            </div>
          </div>

          {/* ═══ ACCIONES ═══ */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
            <button className="sw-btn sw-btn-primario" onClick={() => { limpiarFormulario(); setMostrarFormulario(true); }}>
              ＋ Nuevo artículo o servicio
            </button>
            <button className="sw-btn sw-btn-secundario" onClick={exportarExcel}>
              📊 Exportar Excel
            </button>
            <button className="sw-btn sw-btn-secundario" onClick={() => document.getElementById("archivoExcelInv").click()}>
              📥 Importar Excel
            </button>
            <button className="sw-btn sw-btn-secundario" onClick={mostrarGuia}>
              ❓ Guía importación
            </button>
            <button className="sw-btn" style={{ background: "#fef2f2", color: "#ef4444", border: "1px solid #fecaca" }} onClick={borrarTodo}>
              🗑️ Eliminar todo
            </button>
          </div>

          <input type="file" accept=".xlsx,.xls" onChange={importarDesdeExcel} id="archivoExcelInv" style={{ display: "none" }} />

          {/* ═══ FORMULARIO COMPLETO ═══ */}
          {mostrarFormulario && (
            <div className="sw-card" style={{ marginBottom: 16 }}>
              <div className="sw-card-header" style={{
                background: form.tipo === "servicio"
                  ? "linear-gradient(135deg, #7c3aed, #6d28d9)"
                  : "linear-gradient(135deg, var(--sw-cyan), var(--sw-azul))",
                borderBottom: "none"
              }}>
                <h3 className="sw-card-titulo" style={{ color: "white", margin: 0 }}>
                  {editandoId
                    ? `✏️ Editar ${form.tipo === "servicio" ? "Servicio" : "Artículo"}`
                    : `➕ Nuevo ${form.tipo === "servicio" ? "Servicio" : "Artículo"}`
                  }
                </h3>
              </div>
              <div className="sw-card-body">
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>

                  {/* Selector de tipo */}
                  <div style={{ gridColumn: "1 / -1" }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: "var(--sw-texto-secundario)", marginBottom: 6, display: "block" }}>Tipo *</label>
                    <div style={{ display: "flex", gap: 8 }}>
                      {[
                        { id: "articulo", label: "📦 Artículo", desc: "Producto físico con stock" },
                        { id: "servicio", label: "🔧 Servicio", desc: "Servicio sin stock físico" },
                      ].map((t) => (
                        <button key={t.id} type="button"
                          onClick={() => setForm({ ...form, tipo: t.id, stock: t.id === "servicio" ? 999 : (form.tipo === "servicio" ? "" : form.stock) })}
                          style={{
                            flex: 1, padding: "10px 14px", borderRadius: 10, cursor: "pointer",
                            border: form.tipo === t.id ? `2px solid ${t.id === "servicio" ? "#7c3aed" : "var(--sw-azul)"}` : "1px solid var(--sw-borde)",
                            background: form.tipo === t.id ? (t.id === "servicio" ? "#f3e8ff" : "var(--sw-cyan-muy-claro)") : "white",
                            transition: "all 0.15s",
                          }}
                        >
                          <div style={{ fontWeight: 600, fontSize: 14 }}>{t.label}</div>
                          <div style={{ fontSize: 11, color: "var(--sw-texto-terciario)", marginTop: 2 }}>{t.desc}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div style={{ gridColumn: "1 / -1" }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: "var(--sw-texto-secundario)", marginBottom: 4, display: "block" }}>Nombre *</label>
                    <input type="text" placeholder={form.tipo === "servicio" ? "Ej: Montaje de evento, Arreglo de globos..." : "Nombre del producto"} value={form.nombre}
                      onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                      style={{ width: "100%", padding: "10px 12px", border: "1px solid var(--sw-borde)", borderRadius: 8, fontSize: 14, boxSizing: "border-box" }}
                    />
                  </div>
                  <div style={{ gridColumn: "1 / -1" }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: "var(--sw-texto-secundario)", marginBottom: 4, display: "block" }}>Descripción</label>
                    <input type="text" placeholder="Descripción opcional" value={form.descripcion}
                      onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
                      style={{ width: "100%", padding: "10px 12px", border: "1px solid var(--sw-borde)", borderRadius: 8, fontSize: 14, boxSizing: "border-box" }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, color: "var(--sw-texto-secundario)", marginBottom: 4, display: "block" }}>Precio de venta *</label>
                    <input type="number" placeholder="0" value={form.precio}
                      onChange={(e) => setForm({ ...form, precio: e.target.value })}
                      style={{ width: "100%", padding: "10px 12px", border: "1px solid var(--sw-borde)", borderRadius: 8, fontSize: 14, boxSizing: "border-box" }}
                    />
                  </div>
                  {form.tipo === "articulo" ? (
                    <div>
                      <label style={{ fontSize: 12, fontWeight: 600, color: "var(--sw-texto-secundario)", marginBottom: 4, display: "block" }}>Stock *</label>
                      <input type="number" placeholder="0" value={form.stock}
                        onChange={(e) => setForm({ ...form, stock: e.target.value })}
                        style={{ width: "100%", padding: "10px 12px", border: "1px solid var(--sw-borde)", borderRadius: 8, fontSize: 14, boxSizing: "border-box" }}
                      />
                    </div>
                  ) : (
                    <div>
                      <label style={{ fontSize: 12, fontWeight: 600, color: "var(--sw-texto-secundario)", marginBottom: 4, display: "block" }}>Stock</label>
                      <input type="text" value="∞ Ilimitado" disabled
                        style={{ width: "100%", padding: "10px 12px", border: "1px solid var(--sw-borde)", borderRadius: 8, fontSize: 14, boxSizing: "border-box", background: "#f3f4f6", color: "#9ca3af" }}
                      />
                    </div>
                  )}
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, color: "var(--sw-texto-secundario)", marginBottom: 4, display: "block" }}>
                      Costo interno {form.tipo === "servicio" ? "(producción)" : "(opcional)"}
                    </label>
                    <input type="number" placeholder="0" value={form.costo}
                      onChange={(e) => setForm({ ...form, costo: e.target.value })}
                      style={{ width: "100%", padding: "10px 12px", border: "1px solid var(--sw-borde)", borderRadius: 8, fontSize: 14, boxSizing: "border-box" }}
                    />
                    <div style={{ fontSize: 11, color: "var(--sw-texto-terciario)", marginTop: 3 }}>
                      {form.tipo === "servicio"
                        ? "Lo que te cuesta prestar este servicio (materiales, ayudantes, etc.)"
                        : "Lo que te cuesta este artículo (compra, mantenimiento, etc.)"
                      }
                    </div>
                  </div>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, color: "var(--sw-texto-secundario)", marginBottom: 4, display: "block" }}>Categoría</label>
                    <input type="text" placeholder={form.tipo === "servicio" ? "Ej: Montaje, Decoración, Transporte..." : "Ej: Mobiliario, Decoración, Carpas..."} value={form.categoria}
                      onChange={(e) => setForm({ ...form, categoria: e.target.value })}
                      list="categorias-list"
                      style={{ width: "100%", padding: "10px 12px", border: "1px solid var(--sw-borde)", borderRadius: 8, fontSize: 14, boxSizing: "border-box" }}
                    />
                    <datalist id="categorias-list">
                      {categoriasUnicas.map((cat) => <option key={cat} value={cat} />)}
                    </datalist>
                  </div>
                </div>

                {/* ═══ VALOR DE ADQUISICIÓN (solo artículos) ═══ */}
                {form.tipo === "articulo" && (
                  <div style={{
                    marginTop: 12, padding: "14px", borderRadius: 10,
                    background: "linear-gradient(135deg, #fefce8, #fef9c3)",
                    border: "1px solid #fde68a",
                  }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#92400e", marginBottom: 10 }}>
                      💰 Valor de adquisición
                    </div>
                    <div style={{ fontSize: 11, color: "#a16207", marginBottom: 10 }}>
                      Registra cuánto pagaste por este artículo. Esto permite calcular el valor real de tu inventario.
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                      <div>
                        <label style={{ fontSize: 12, fontWeight: 600, color: "#92400e", marginBottom: 4, display: "block" }}>
                          Valor por unidad ($)
                        </label>
                        <input type="number" placeholder="0" value={form.valor_adquisicion}
                          onChange={(e) => setForm({ ...form, valor_adquisicion: e.target.value })}
                          style={{ width: "100%", padding: "10px 12px", border: "1px solid #fde68a", borderRadius: 8, fontSize: 14, boxSizing: "border-box", background: "white" }}
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: 12, fontWeight: 600, color: "#92400e", marginBottom: 4, display: "block" }}>
                          Fecha de compra
                        </label>
                        <input type="date" value={form.fecha_adquisicion}
                          onChange={(e) => setForm({ ...form, fecha_adquisicion: e.target.value })}
                          style={{ width: "100%", padding: "10px 12px", border: "1px solid #fde68a", borderRadius: 8, fontSize: 14, boxSizing: "border-box", background: "white" }}
                        />
                      </div>
                    </div>
                    {/* Checkbox registrar gasto — solo al crear, no al editar */}
                    {!editandoId && (
                      <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 8 }}>
                        <input
                          type="checkbox"
                          id="registrar-gasto-check"
                          checked={form.registrar_gasto_compra}
                          onChange={(e) => setForm({ ...form, registrar_gasto_compra: e.target.checked })}
                          style={{ width: 18, height: 18, cursor: "pointer", accentColor: "#f59e0b" }}
                        />
                        <label htmlFor="registrar-gasto-check" style={{ fontSize: 12, color: "#92400e", cursor: "pointer" }}>
                          Registrar como gasto en contabilidad (artículo nuevo, no existía antes)
                        </label>
                      </div>
                    )}
                    {form.registrar_gasto_compra && Number(form.valor_adquisicion || 0) > 0 && Number(form.stock || 0) > 0 && (
                      <div style={{ marginTop: 8, padding: "8px 12px", borderRadius: 6, background: "#fef2f2", border: "1px solid #fecaca", fontSize: 12, color: "#991b1b" }}>
                        Se registrará un gasto de <strong>{money(Number(form.valor_adquisicion) * Number(form.stock))}</strong> en contabilidad al guardar
                      </div>
                    )}
                  </div>
                )}

                {/* Margen calculado */}
                {Number(form.precio || 0) > 0 && Number(form.costo || 0) > 0 && (
                  <div style={{
                    marginTop: 12, padding: "10px 14px", borderRadius: 8,
                    background: "#f0fdf4", border: "1px solid #bbf7d0",
                    fontSize: 13, color: "#166534", display: "flex", gap: 16, flexWrap: "wrap",
                  }}>
                    <span>💰 Ganancia estimada: <strong>{money(Number(form.precio) - Number(form.costo))}</strong></span>
                    <span>📊 Margen: <strong>{Math.round(((Number(form.precio) - Number(form.costo)) / Number(form.precio)) * 100)}%</strong></span>
                  </div>
                )}

                <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
                  <button className="sw-btn sw-btn-primario" style={{ flex: 1 }} onClick={guardarProducto}>
                    {editandoId ? "💾 Actualizar" : "💾 Guardar"}
                  </button>
                  <button className="sw-btn sw-btn-secundario" style={{ flex: 1 }} onClick={limpiarFormulario}>
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ═══ BÚSQUEDA Y FILTROS ═══ */}
          <div className="sw-card" style={{ marginBottom: 16 }}>
            <div className="sw-card-body" style={{ padding: "12px 16px" }}>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                {/* Búsqueda */}
                <div style={{ position: "relative", flex: "1 1 250px", minWidth: 200 }}>
                  <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", fontSize: 16, color: "var(--sw-texto-terciario)" }}>🔍</span>
                  <input
                    type="text"
                    placeholder="Buscar por nombre, descripción, categoría..."
                    value={buscar}
                    onChange={(e) => setBuscar(e.target.value)}
                    style={{
                      width: "100%", padding: "10px 12px 10px 38px",
                      border: "1px solid var(--sw-borde)", borderRadius: 8,
                      fontSize: 14, boxSizing: "border-box",
                      background: "var(--sw-fondo)"
                    }}
                  />
                </div>
                {/* Filtro categoría */}
                <select
                  value={categoriaFiltro}
                  onChange={(e) => setCategoriaFiltro(e.target.value)}
                  style={{
                    padding: "10px 12px", border: "1px solid var(--sw-borde)",
                    borderRadius: 8, fontSize: 14, background: "var(--sw-fondo)",
                    color: "var(--sw-texto)", minWidth: 160
                  }}
                >
                  <option value="">Todas las categorías</option>
                  {categoriasUnicas.map((cat) => <option key={cat} value={cat}>{cat}</option>)}
                </select>
              </div>
              {(buscar || categoriaFiltro) && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 12, color: "var(--sw-texto-terciario)" }}>
                    {productosFiltrados.length} de {productos.length} items
                  </span>
                  <button
                    onClick={() => { setBuscar(""); setCategoriaFiltro(""); }}
                    style={{
                      fontSize: 11, padding: "2px 10px", borderRadius: 20,
                      background: "#fef2f2", color: "#ef4444", border: "1px solid #fecaca",
                      cursor: "pointer"
                    }}
                  >
                    ✕ Limpiar filtros
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* ═══ LISTADO CON BADGES DE TIPO, ADQ, COSTO, MARGEN ═══ */}
          <div className="sw-card">
            <div className="sw-card-header">
              <h3 className="sw-card-titulo">
                📋 {buscar || categoriaFiltro ? "Resultados" : "Todos los productos"} ({productosFiltrados.length})
              </h3>
            </div>
            <div className="sw-card-body" style={{ padding: 0 }}>
              {loading ? (
                <div style={{ padding: 40, textAlign: "center", color: "var(--sw-texto-terciario)" }}>
                  Cargando inventario...
                </div>
              ) : productosFiltrados.length === 0 ? (
                <div style={{ padding: 40, textAlign: "center" }}>
                  <div style={{ fontSize: 40, marginBottom: 8 }}>📦</div>
                  <div style={{ fontSize: 14, color: "var(--sw-texto-terciario)" }}>
                    {buscar || categoriaFiltro ? "No se encontraron items con esos filtros" : "No hay productos ni servicios registrados"}
                  </div>
                  {!buscar && !categoriaFiltro && (
                    <p style={{ fontSize: 12, color: "var(--sw-texto-terciario)", marginTop: 6 }}>
                      Agrega tu primer artículo o servicio, o importa desde Excel
                    </p>
                  )}
                </div>
              ) : (
                productosFiltrados.map((p) => {
                  const esServicio = p.tipo === "servicio";
                  return (
                    <div key={p.id} style={{
                      padding: "14px 16px",
                      borderBottom: "1px solid #f3f4f6",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 10,
                      transition: "background 0.15s",
                    }}
                      onMouseEnter={(e) => e.currentTarget.style.background = "#f9fafb"}
                      onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                    >
                      {/* Info */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                          <span style={{ fontWeight: 600, fontSize: 14, color: "var(--sw-texto)" }}>
                            {p.nombre}
                          </span>
                          <span style={{
                            fontSize: 10, padding: "2px 8px", borderRadius: 20, fontWeight: 600,
                            background: esServicio ? "#f3e8ff" : "#eff6ff",
                            color: esServicio ? "#7c3aed" : "#2563eb",
                          }}>
                            {esServicio ? "🔧 Servicio" : "📦 Artículo"}
                          </span>
                          {p.categoria && (
                            <span style={{
                              fontSize: 11, padding: "2px 8px", borderRadius: 20,
                              background: "var(--sw-cyan-muy-claro)", color: "var(--sw-azul)",
                              fontWeight: 500
                            }}>
                              {p.categoria}
                            </span>
                          )}
                          {!esServicio && Number(p.stock || 0) === 0 && (
                            <span style={{
                              fontSize: 10, padding: "2px 8px", borderRadius: 20,
                              background: "#fef2f2", color: "#ef4444", fontWeight: 600
                            }}>
                              Sin stock
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: 12, color: "var(--sw-texto-terciario)", marginTop: 3, display: "flex", flexWrap: "wrap", gap: "4px 14px" }}>
                          <span style={{ fontWeight: 600, color: "var(--sw-verde-oscuro)" }}>{money(p.precio)}</span>
                          {!esServicio && <span>Stock: <strong>{Number(p.stock || 0)}</strong></span>}
                          {Number(p.valor_adquisicion || 0) > 0 && <span style={{ color: "#b45309" }}>Adq: {money(p.valor_adquisicion)}</span>}
                          {Number(p.costo || 0) > 0 && <span style={{ color: "#dc2626" }}>Costo: {money(p.costo)}</span>}
                          {Number(p.costo || 0) > 0 && Number(p.precio || 0) > 0 && (
                            <span style={{ color: "var(--sw-verde)", fontWeight: 500 }}>
                              Margen: {Math.round(((Number(p.precio) - Number(p.costo)) / Number(p.precio)) * 100)}%
                            </span>
                          )}
                          {p.descripcion && <span style={{ color: "#9ca3af" }}>{p.descripcion}</span>}
                        </div>
                      </div>

                      {/* Acciones */}
                      <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                        {!esServicio && (
                          <button className="sw-btn-icono" onClick={() => registrarCompra(p)}
                            title="Registrar compra de más unidades"
                            style={{ color: "#16a34a" }}
                          >
                            ➕
                          </button>
                        )}
                        <button className="sw-btn-icono" onClick={() => editarProducto(p)} title="Editar">
                          ✏️
                        </button>
                        <button className="sw-btn-icono" onClick={() => eliminarProducto(p.id)} title="Eliminar"
                          style={{ color: "#ef4444" }}
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          </>
          )}

          {/* ═══ MODAL PAQUETE ═══ */}
          {modalPaquete && (
            <EditarPaqueteModal
              paqueteEnEdicion={paqueteEditar}
              onGuardar={obtenerPaquetes}
              onClose={() => { setModalPaquete(false); setPaqueteEditar(null); }}
            />
          )}

        </div>
      </div>
    </Protegido>
  );
}